// src/TenderDetails.js
import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  query,
  orderBy,
  setDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "./firebase";
import "./styles.css";
import Review from "./review";
import CostingSheet from "./CostingSheet";
import VendorAutomation from "./VendorAutomation";

import DiagramsNetEditor from "./DiagramsNetEditor";
import SimpleProposalGenerator from "./SimpleProposalGenerator";
import DocumentManager from "./DocumentManager";

import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

// Set pdfjs worker AFTER imports (keeps all imports at top for eslint/import/first)
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Constants for PDF rendering and highlight calculations
const PDF_RENDER_WIDTH = 600; // Width in pixels for PDF page rendering
const MIN_HIGHLIGHT_HEIGHT = 10; // Minimum height for visibility of highlights
const PDF_PAGE_SELECTOR = '.react-pdf__Page'; // CSS selector for PDF page element
const PDF_TEXT_LAYER_SELECTOR = '.react-pdf__Page__textContent'; // CSS selector for PDF text layer

// Constants for Knowledge Base
const KB_DOC_ID_MAX_LENGTH = 100; // Maximum length for knowledge base document ID
const KB_MIN_MATCHING_WORDS = 3; // Minimum number of words that must match
const KB_MATCHING_WORD_RATIO = 0.4; // Percentage of search words that must match
const KB_MAX_SUGGESTIONS = 5; // Maximum number of suggestions to return

// Constants for Scanned PDF Detection
const SCANNED_PDF_NOTICE_AUTO_HIDE_DELAY = 10000; // Auto-hide scanned PDF notice after 10 seconds
const PDF_RENDER_DELAY_MS = 1000; // Delay before checking for scanned PDF to allow page rendering

/**
 * TenderDetails - full component
 *
 * - fetches tender and clauses from Firestore
 * - handles file upload/delete (Firebase Storage)
 * - shows PDF viewer and allows selecting text for clause extraction
 * - passes persistence handlers to Review component
 */

async function extractCurrentPageText(pdfUrl, pageNum) {
  try {
    const loadingTask = pdfjs.getDocument(pdfUrl);
    const pdf = await loadingTask.promise;

    if (pageNum < 1 || pageNum > pdf.numPages) return null;

    const page = await pdf.getPage(pageNum);
    const textContent = await page.getTextContent();
    const strings = textContent.items.map((item) => item.str);

    return `--- PAGE ${pageNum} ---\n\n` + strings.join(" ");
  } catch (error) {
    console.error("Error extracting page text:", error);
    return null;
  }
}

/**
 * Check if a PDF page is scanned by checking if it has text content
 * Uses pdfjs getTextContent() to detect scanned pages
 */
async function checkIfPageIsScanned(pdfUrl, pageNum) {
  try {
    const loadingTask = pdfjs.getDocument(pdfUrl);
    const pdf = await loadingTask.promise;

    if (pageNum < 1 || pageNum > pdf.numPages) return false;

    const page = await pdf.getPage(pageNum);
    const textContent = await page.getTextContent();

    // If no text items, it's a scanned page
    return textContent.items.length === 0;
  } catch (error) {
    console.error("Error checking if page is scanned:", error);
    return false;
  }
}

/**
 * Render a PDF page to a canvas and convert to base64 image
 * This is done on the client side for better performance
 */
async function renderPageToImage(pdfUrl, pageNum) {
  try {
    const loadingTask = pdfjs.getDocument(pdfUrl);
    const pdf = await loadingTask.promise;

    if (pageNum < 1 || pageNum > pdf.numPages) return null;

    const page = await pdf.getPage(pageNum);
    const viewport = page.getViewport({ scale: 2 }); // 2x scale for better quality

    // Create a temporary canvas
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;

    const context = canvas.getContext('2d');

    // Render the page to canvas
    await page.render({
      canvasContext: context,
      viewport: viewport
    }).promise;

    // Convert canvas to base64 data URL
    return canvas.toDataURL('image/png');
  } catch (error) {
    console.error("Error rendering page to image:", error);
    return null;
  }
}

/**
 * Find text position on PDF page using the rendered text layer
 * This provides pin-point accuracy by using actual DOM element positions
 * @param {number} pageNum - Page number to search
 * @param {string} searchText - Text to find on the page
 * @returns {Object|null} - Highlight rectangle {top, left, width, height} or null if not found
 */
function findTextPositionInRenderedPage(pageNum, searchText) {
  try {
    // Get the rendered text layer from react-pdf
    const textLayer = document.querySelector(PDF_TEXT_LAYER_SELECTOR);
    if (!textLayer) {
      console.warn('Text layer not found');
      return null;
    }

    const pageElement = document.querySelector(PDF_PAGE_SELECTOR);
    if (!pageElement) {
      console.warn('Page element not found');
      return null;
    }

    const pageRect = pageElement.getBoundingClientRect();

    // Get all text spans in the text layer
    const textSpans = Array.from(textLayer.querySelectorAll('span'));
    
    if (textSpans.length === 0) {
      console.warn('No text spans found in text layer');
      return null;
    }
    
    // Multiple normalization functions for different matching strategies
    const normalizeBasic = (text) => text.toLowerCase().replace(/\s+/g, ' ').trim();
    const normalizeStrict = (text) => text.toLowerCase().replace(/[^a-z0-9]/g, '');
    const normalizeWords = (text) => text.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
    
    // Remove leading numbering pattern (e.g., "1.2.3. " or "10.1 ")
    const removeLeadingNumbering = (text) => {
      return text.replace(/^[\d.\s\-)]+/, '').trim();
    };
    
    const cleanSearchText = normalizeBasic(searchText);
    const strictSearchText = normalizeStrict(searchText);
    const wordsSearchText = normalizeWords(searchText);
    
    // Get the actual content part (without leading numbers) for length calculation
    const contentSearchText = removeLeadingNumbering(cleanSearchText);
    const targetLength = Math.max(cleanSearchText.length, contentSearchText.length + 20); // Ensure we cover full clause
    
    if (!cleanSearchText || cleanSearchText.length < 3) {
      console.warn('Search text too short');
      return null;
    }

    // Build concatenated page text for substring search
    let fullPageText = '';
    const spanTextMap = []; // Maps position in fullPageText to span index
    
    textSpans.forEach((span, spanIdx) => {
      const text = span.textContent || '';
      if (text) {
        const startPos = fullPageText.length;
        fullPageText += text;
        spanTextMap.push({ spanIdx, startPos, endPos: fullPageText.length, span });
      }
    });
    
    const normalizedPageText = normalizeBasic(fullPageText);
    const strictPageText = normalizeStrict(fullPageText);
    const wordsPageText = normalizeWords(fullPageText);
    
    // MULTIPLE MATCHING STRATEGIES
    let matchingSpans = [];
    let matchFound = false;
    
    // Strategy 1: Find exact substring in page text
    const trySubstringMatch = (searchStr, pageStr, prefixLen) => {
      const prefix = searchStr.substring(0, Math.min(prefixLen, searchStr.length));
      const matchIdx = pageStr.indexOf(prefix);
      if (matchIdx !== -1) {
        return matchIdx;
      }
      return -1;
    };
    
    // Also try matching without leading numbering
    const contentWordsSearch = normalizeWords(contentSearchText);
    
    // Try with different prefix lengths and normalizations
    const strategies = [
      { search: cleanSearchText, page: normalizedPageText, prefixLens: [50, 40, 30, 20, 15, 10] },
      { search: wordsSearchText, page: wordsPageText, prefixLens: [40, 30, 20, 15, 10] },
      { search: contentWordsSearch, page: wordsPageText, prefixLens: [40, 30, 20, 15] }, // Match content without numbering
      { search: strictSearchText, page: strictPageText, prefixLens: [30, 20, 15, 10] }
    ];
    
    let foundMatchIdx = -1;
    let usedStrategy = null;
    
    for (const strategy of strategies) {
      if (!strategy.search || strategy.search.length < 5) continue; // Skip if search text too short
      for (const prefixLen of strategy.prefixLens) {
        foundMatchIdx = trySubstringMatch(strategy.search, strategy.page, prefixLen);
        if (foundMatchIdx !== -1) {
          usedStrategy = strategy;
          break;
        }
      }
      if (foundMatchIdx !== -1) break;
    }
    
    if (foundMatchIdx !== -1 && usedStrategy) {
      // Find which spans contain the match
      // Map the match position back to original text position (approximate)
      const ratio = foundMatchIdx / usedStrategy.page.length;
      const approxOriginalPos = Math.floor(ratio * fullPageText.length);
      const matchLength = Math.min(targetLength, fullPageText.length - approxOriginalPos);
      
      // Find spans that overlap with the match range
      for (const item of spanTextMap) {
        if (item.startPos < approxOriginalPos + matchLength && item.endPos > approxOriginalPos) {
          matchingSpans.push(item.span);
        }
      }
      
      // Extend to cover the full clause length - use targetLength which accounts for numbering
      if (matchingSpans.length > 0) {
        const lastMatchedIdx = spanTextMap.findIndex(item => item.span === matchingSpans[matchingSpans.length - 1]);
        let consumedLength = matchingSpans.reduce((sum, span) => sum + (span.textContent?.length || 0), 0);
        
        // Continue adding spans until we've covered enough text
        // Use 90% threshold to ensure we get the full content
        for (let i = lastMatchedIdx + 1; i < spanTextMap.length && consumedLength < targetLength * 0.9; i++) {
          matchingSpans.push(spanTextMap[i].span);
          consumedLength += spanTextMap[i].span.textContent?.length || 0;
        }
        matchFound = true;
      }
    }
    
    // Strategy 2: Word-based matching (fallback) - skip numbering, match content words
    if (!matchFound) {
      // Use content words (without leading numbers)
      const clauseWords = normalizeWords(contentSearchText).split(' ').filter(w => w.length > 3);
      if (clauseWords.length >= 2) {
        // Look for spans containing multiple clause words
        for (let i = 0; i < textSpans.length; i++) {
          const spanText = normalizeWords(textSpans[i].textContent || '');
          const wordsInSpan = clauseWords.filter(w => spanText.includes(w));
          
          if (wordsInSpan.length >= 2 || (wordsInSpan.length >= 1 && clauseWords.length <= 3)) {
            // Found a starting point, now greedily consume
            let consumedLength = 0;
            for (let j = i; j < textSpans.length && consumedLength < targetLength * 0.9; j++) {
              matchingSpans.push(textSpans[j]);
              consumedLength += (textSpans[j].textContent?.length || 0) + 1;
            }
            matchFound = true;
            break;
          }
        }
      }
    }
    
    // Strategy 3: First significant word match (last resort) - use content without numbering
    if (!matchFound) {
      const contentWords = normalizeWords(contentSearchText).split(' ');
      const firstSignificantWord = contentWords.find(w => w.length > 5) || contentWords.find(w => w.length > 3);
      if (firstSignificantWord) {
        for (let i = 0; i < textSpans.length; i++) {
          const spanText = normalizeWords(textSpans[i].textContent || '');
          if (spanText.includes(firstSignificantWord)) {
            // Found starting point
            let consumedLength = 0;
            for (let j = i; j < textSpans.length && consumedLength < targetLength * 0.9; j++) {
              matchingSpans.push(textSpans[j]);
              consumedLength += (textSpans[j].textContent?.length || 0) + 1;
            }
            matchFound = true;
            break;
          }
        }
      }
    }
    
    if (matchingSpans.length === 0) {
      console.warn('No matching spans found with any strategy:', cleanSearchText.substring(0, 30));
      return null;
    }
    
    console.log(`Match found: ${matchingSpans.length} spans for clause`);

    // Calculate bounding box from actual rendered positions
    let minTop = Infinity, maxBottom = -Infinity, minLeft = Infinity, maxRight = -Infinity;

    matchingSpans.forEach((span) => {
      const rect = span.getBoundingClientRect();
      // Only include spans that have actual dimensions
      if (rect.width > 0 && rect.height > 0) {
        minTop = Math.min(minTop, rect.top);
        maxBottom = Math.max(maxBottom, rect.bottom);
        minLeft = Math.min(minLeft, rect.left);
        maxRight = Math.max(maxRight, rect.right);
      }
    });

    // Validate we got valid bounds
    if (minTop === Infinity || maxBottom === -Infinity) {
      console.warn('Could not calculate valid bounds');
      return null;
    }

    // Calculate position relative to the page container
    const top = minTop - pageRect.top; 
    const left = minLeft - pageRect.left;
    const width = maxRight - minLeft;
    const height = maxBottom - minTop;

    // Sanity check: cap width to page width, but allow reasonable height for multi-line clauses
    const maxReasonableWidth = pageRect.width;
    const maxReasonableHeight = 400; // Allow up to ~15-20 lines of text
    
    return {
      top: Math.max(0, top),
      left: Math.max(0, left),
      width: Math.min(Math.max(0, width), maxReasonableWidth),
      height: Math.min(Math.max(MIN_HIGHLIGHT_HEIGHT, height), maxReasonableHeight),
    };
  } catch (error) {
    console.error("Error finding text position in rendered page:", error);
    return null;
  }
}

export default function TenderDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  // Tender + clauses state
  const [tender, setTender] = useState(null);
  const [clauses, setClauses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [costingItems, setCostingItems] = useState([]);

  // active tab
  const [activeTab, setActiveTab] = useState("overview");

  // PDF & AI States
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedDocUrl, setSelectedDocUrl] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [showAllClauses, setShowAllClauses] = useState(false);

  // Highlight state for selected text
  const [highlight, setHighlight] = useState(null);
  
  // Drag-and-highlight states
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragRect, setDragRect] = useState(null);
  const pageElementRef = useRef(null); // Cache PDF page element for performance
  const scannedPdfNoticeTimerRef = useRef(null); // Timer ref for auto-dismissing scanned PDF notice banner

  // Manual clause add states
  const [newClauseText, setNewClauseText] = useState("");
  const [newClausePage, setNewClausePage] = useState("1");
  const [newClauseNumber, setNewClauseNumber] = useState("");
  const [showScannedPdfNotice, setShowScannedPdfNotice] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  
  // Full-page OCR extraction states
  const [fullPageText, setFullPageText] = useState(""); // Stores extracted text from scanned PDF
  const [isExtractingFullPage, setIsExtractingFullPage] = useState(false); // Loading state for full-page extraction
  const [showExtractedText, setShowExtractedText] = useState(false); // Toggle to show/hide extracted text panel
  const [isPageScanned, setIsPageScanned] = useState(false); // Track if current page is scanned (for button visibility)

  // Edit/Upload states
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Comment save indicator per clause
  const [isCommentSaving, setIsCommentSaving] = useState({});

  // Compliance Knowledge Base - Central database for comment/response suggestions
  const [complianceKnowledgeBase, setComplianceKnowledgeBase] = useState([]);

  // --- FETCH DATA ---
  const fetchTender = async () => {
    try {
      const docRef = doc(db, "tenders", id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = { id: docSnap.id, ...docSnap.data() };
        setTender(data);
        setEditFormData(data);
        if (data.files && data.files.length > 0) setSelectedDocUrl(data.files[0].url);
      } else {
        alert("Tender not found!");
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("fetchTender error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClauses = async () => {
    try {
      const q = query(collection(db, "tenders", id, "clauses"), orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);
      setClauses(querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error("fetchClauses error:", error);
    }
  };

  // Fetch costing items for Single Line Diagram
  const fetchCostingItems = async () => {
    try {
      const costingRef = collection(db, "tenders", id, "costingItems");
      const costingQuery = query(costingRef, orderBy("createdAt", "asc"));
      const querySnapshot = await getDocs(costingQuery);
      setCostingItems(querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (error) {
      // Fallback: load without orderBy if createdAt field doesn't exist
      try {
        const costingRef = collection(db, "tenders", id, "costingItems");
        const querySnapshot = await getDocs(costingRef);
        setCostingItems(querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (innerError) {
        console.error("fetchCostingItems error:", innerError);
      }
    }
  };

  // Fetch the compliance knowledge base (central database for comment/response suggestions)
  const fetchComplianceKnowledgeBase = async () => {
    try {
      const kbRef = collection(db, "complianceKnowledgeBase");
      const snapshot = await getDocs(kbRef);
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setComplianceKnowledgeBase(data);
    } catch (error) {
      console.error("fetchComplianceKnowledgeBase error:", error);
    }
  };

  // Save clause data to the compliance knowledge base
  const saveToKnowledgeBase = async (clauseText, comment, customerResponse, revisionName) => {
    if (!clauseText || !clauseText.trim()) return;
    // Only save if we have meaningful content
    if ((!comment || !comment.trim()) && (!customerResponse || !customerResponse.trim())) return;
    
    try {
      // Create a normalized key from the clause text for deduplication
      const normalizedClause = clauseText.toLowerCase().replace(/\s+/g, ' ').trim();
      const docId = normalizedClause.substring(0, KB_DOC_ID_MAX_LENGTH).replace(/[^a-z0-9]/g, '_');
      
      const kbRef = doc(db, "complianceKnowledgeBase", docId);
      const existingDoc = await getDoc(kbRef);
      
      const entry = {
        clauseText: clauseText.trim(),
        normalizedClause,
        comment: comment?.trim() || "",
        customerResponse: customerResponse?.trim() || "",
        revision: revisionName || "Rev 0",
        updatedAt: new Date().toISOString(),
      };
      
      if (existingDoc.exists()) {
        // Update existing entry with the latest data
        await setDoc(kbRef, entry, { merge: true });
      } else {
        entry.createdAt = new Date().toISOString();
        await setDoc(kbRef, entry);
      }
      
      // Refresh the knowledge base
      await fetchComplianceKnowledgeBase();
    } catch (error) {
      console.error("saveToKnowledgeBase error:", error);
    }
  };

  // Find similar clauses from the knowledge base
  const findSimilarClauses = (clauseText) => {
    if (!clauseText || !clauseText.trim()) return [];
    
    const normalizedSearch = clauseText.toLowerCase().replace(/\s+/g, ' ').trim();
    const searchWords = normalizedSearch.split(' ').filter(w => w.length > 3);
    
    // Find entries with matching words (simple text-based similarity)
    const matches = complianceKnowledgeBase.filter((entry) => {
      if (!entry.normalizedClause) return false;
      
      // Count matching significant words
      const matchingWords = searchWords.filter(word => 
        entry.normalizedClause.includes(word)
      );
      
      // Require minimum matching words or percentage of search words
      const minMatches = Math.max(KB_MIN_MATCHING_WORDS, Math.floor(searchWords.length * KB_MATCHING_WORD_RATIO));
      return matchingWords.length >= minMatches;
    });
    
    // Sort by number of matching words (descending)
    matches.sort((a, b) => {
      const aMatches = searchWords.filter(w => a.normalizedClause.includes(w)).length;
      const bMatches = searchWords.filter(w => b.normalizedClause.includes(w)).length;
      return bMatches - aMatches;
    });
    
    // Return top suggestions
    return matches.slice(0, KB_MAX_SUGGESTIONS);
  };

  useEffect(() => {
    fetchTender();
    fetchClauses();
    fetchCostingItems();
    fetchComplianceKnowledgeBase();
    
    // Cleanup function to clear timer on unmount
    return () => {
      if (scannedPdfNoticeTimerRef.current) {
        clearTimeout(scannedPdfNoticeTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // --- PDF HIGHLIGHT INJECTION LOGIC ---

  // Helper function to check if a clause belongs to the currently selected document
  // Returns true for legacy clauses without documentUrl (backward compatibility) or clauses matching current document
  const belongsToCurrentDocument = (clause) => !clause.documentUrl || clause.documentUrl === selectedDocUrl;

  // Filter clauses for document-specific highlighting in Compliance tab
  // When "Show All" is checked: Show all pages for current document only
  // When "Show All" is unchecked: Show only current page for current document
  const filteredClauses = showAllClauses 
    ? clauses.filter(belongsToCurrentDocument)
    : clauses.filter((c) => String(c.page) === String(pageNumber) && belongsToCurrentDocument(c));

  useEffect(() => {
    const pageElement = document.querySelector(PDF_PAGE_SELECTOR);
    if (!pageElement) return;

    // 1. Clear existing highlights (identified by the custom class)
    pageElement.querySelectorAll('.app-highlight').forEach(el => el.remove());

    const highlightsToRender = [];

    // 2. Add the active drag rectangle (shown during dragging)
    if (isDragging && dragRect) {
      highlightsToRender.push({ ...dragRect, isDragging: true });
    }

    // 3. Add the temporary user selection highlight
    if (highlight) {
      highlightsToRender.push({ ...highlight, isSelection: true });
    }

    // 4. Add all saved clause highlights for the current page
    filteredClauses.forEach(clause => {
      if (clause.highlightRect && String(clause.page) === String(pageNumber)) {
        highlightsToRender.push({ ...clause.highlightRect, status: clause.status });
      }
    });

    // 5. Create and inject the new highlight DOM elements
    highlightsToRender.forEach(rect => {
      const highlightDiv = document.createElement('div');
      highlightDiv.className = `app-highlight pdf-highlight`;
      highlightDiv.key = Math.random(); // Unique key for DOM injection

      // Apply coordinates (in un-scaled pixels relative to pageElement)
      highlightDiv.style.position = 'absolute';
      highlightDiv.style.top = `${rect.top}px`;
      highlightDiv.style.left = `${rect.left}px`;
      highlightDiv.style.width = `${rect.width}px`;
      highlightDiv.style.height = `${rect.height}px`;
      highlightDiv.style.pointerEvents = 'none';
      highlightDiv.style.borderRadius = '3px';
      
      // Apply status color logic
      if (rect.isDragging) {
        // Active drag rectangle - lighter/more transparent
        highlightDiv.style.backgroundColor = 'rgba(135, 206, 250, 0.25)';
        highlightDiv.style.border = '2px dashed rgba(30, 144, 255, 0.6)';
        highlightDiv.style.zIndex = '9';
      } else if (rect.isSelection) {
        highlightDiv.style.backgroundColor = 'rgba(255, 255, 0, 0.3)';
        highlightDiv.style.zIndex = '10';
        highlightDiv.style.border = 'none';
      } else {
        highlightDiv.style.zIndex = '15';
        let bgColor = 'transparent';
        let borderColor = 'none';

        switch (rect.status) {
          case "Complied":
            bgColor = "rgba(0, 150, 0, 0.3)";
            borderColor = "1px solid rgba(0,150,0,0.5)";
            break;
          case "Non-Complied":
            bgColor = "rgba(255, 0, 0, 0.3)";
            borderColor = "1px solid rgba(255,0,0,0.5)";
            break;
          case "Pending":
            bgColor = "rgba(255, 165, 0, 0.3)";
            borderColor = "1px solid rgba(255,165,0,0.5)";
            break;
          case "Partially Complied":
            bgColor = "rgba(0, 120, 255, 0.3)";
            borderColor = "1px solid rgba(0,120,255,0.5)";
            break;
          case "Not Applicable":
            bgColor = "rgba(128, 128, 128, 0.3)";
            borderColor = "1px solid rgba(128,128,128,0.5)";
            break;
          default:
            bgColor = "rgba(255, 165, 0, 0.3)";
            borderColor = "1px solid rgba(255,165,0,0.5)";
        }
        highlightDiv.style.backgroundColor = bgColor;
        highlightDiv.style.border = borderColor;
      }

      pageElement.appendChild(highlightDiv);
    });
    
    // Clean up if component unmounts
    return () => {
      pageElement.querySelectorAll('.app-highlight').forEach(el => el.remove());
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, highlight, clauses.length, isDragging, dragRect]); 

  // --- PDF HANDLING ---
  // Ref to track if component is mounted (for cleanup)
  const isMountedRef = useRef(true);
  
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  /**
   * Check if the current PDF page is scanned (for detection only, not extraction)
   * Sets isPageScanned state to show/hide manual extraction button
   */
  const checkAndDetectScannedPage = async () => {
    if (!selectedDocUrl) {
      console.log('No selectedDocUrl, skipping scanned page detection');
      return;
    }
    
    try {
      console.log('Checking if page', pageNumber, 'is scanned for URL:', selectedDocUrl);
      
      // Check if page is scanned using pdfjs
      const isScanned = await checkIfPageIsScanned(selectedDocUrl, pageNumber);
      
      console.log('isPageScanned result:', isScanned, 'isMountedRef.current:', isMountedRef.current);
      
      if (isMountedRef.current) {
        setIsPageScanned(isScanned);
        console.log('Set isPageScanned state to:', isScanned);
        
        if (isScanned) {
          console.log('Scanned PDF detected on page', pageNumber, '- button should be visible');
        } else {
          console.log('Regular PDF detected on page', pageNumber, '- button should be hidden');
          // Clear any previously extracted text when switching to regular PDF
          setFullPageText("");
          setShowExtractedText(false);
        }
      }
    } catch (error) {
      console.error("Error checking for scanned PDF:", error);
      if (isMountedRef.current) {
        setIsPageScanned(false);
      }
    }
  };
  
  /**
   * Manually trigger full-page OCR extraction for the current page
   * Called when user clicks the "Extract Text" button
   */
  const handleManualFullPageExtraction = async () => {
    if (!selectedDocUrl) return;
    
    try {
      console.log('Manually triggering full-page extraction for page', pageNumber);
      
      // Check if component is still mounted before updating state
      if (!isMountedRef.current) return;
      setIsExtractingFullPage(true);
      setFullPageText(""); // Clear previous text
      
      try {
        // Render page to canvas and get base64 image
        const imageBase64 = await renderPageToImage(selectedDocUrl, pageNumber);
        
        if (!imageBase64) {
          throw new Error('Failed to render page to image');
        }
        
        if (!isMountedRef.current) return;
        
        // Call the OCR API with base64 image (client-side rendering mode)
        const response = await fetch("/api/ocr-extract-text", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ 
            imageBase64: imageBase64,
            pageNumber: pageNumber
          }),
        });
        
        if (response.ok) {
          const data = await response.json();
          if (!isMountedRef.current) return;
          
          if (data.success && data.text) {
            setFullPageText(data.text);
            setShowExtractedText(true); // Auto-expand after manual extraction
            console.log('Full page extraction successful, text length:', data.text.length);
          } else {
            console.warn('OCR returned no text for full page');
            alert('No text could be extracted from this page.');
          }
        } else {
          const errorData = await response.json().catch((parseError) => ({ 
            error: `Failed to parse error response`,
            details: `Status: ${response.status}, Parse error: ${parseError.message}` 
          }));
          console.error('Full page OCR error:', errorData);
          alert(`OCR extraction failed: ${errorData.error || 'Unknown error'}`);
        }
      } catch (error) {
        console.error("Full page OCR extraction error:", error);
        alert(`OCR extraction failed: ${error.message}`);
      } finally {
        if (isMountedRef.current) {
          setIsExtractingFullPage(false);
        }
      }
    } catch (error) {
      console.error("Error in manual full-page extraction:", error);
      if (isMountedRef.current) {
        setIsExtractingFullPage(false);
      }
    }
  };
  
  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setPageNumber(1);
    setNewClausePage("1");
    // Check if first page is scanned (detection only, no auto-extraction)
    // Use setTimeout to ensure PDF is fully loaded
    setTimeout(() => {
      checkAndDetectScannedPage().catch((error) => {
        console.error("Error in checkAndDetectScannedPage:", error);
      });
    }, PDF_RENDER_DELAY_MS);
  }

  function onDocumentLoadError(error) {
    console.error("Error loading PDF:", error);
  }

  // Drag-and-highlight handlers
  const handleMouseDown = (e) => {
    // Only start dragging if clicking on the PDF page itself, not on buttons or controls
    const pageElement = document.querySelector(PDF_PAGE_SELECTOR);
    if (!pageElement) return;
    
    // Cache the page element reference for performance
    pageElementRef.current = pageElement;
    
    const pageRect = pageElement.getBoundingClientRect();
    
    // Check if the click is within the PDF page
    if (e.clientX < pageRect.left || e.clientX > pageRect.right ||
        e.clientY < pageRect.top || e.clientY > pageRect.bottom) {
      return;
    }
    
    const startX = e.clientX - pageRect.left;
    const startY = e.clientY - pageRect.top;
    
    // Store the start position but don't set isDragging yet
    // We'll only start dragging if the mouse moves significantly
    setDragStart({ x: startX, y: startY });
    setDragRect(null);
  };
  
  const handleMouseMove = (e) => {
    if (!dragStart) return;
    
    // Use cached reference to avoid repeated DOM queries
    const pageElement = pageElementRef.current;
    if (!pageElement) {
      // If cache is missing, cancel the drag operation
      setIsDragging(false);
      setDragStart(null);
      return;
    }
    
    const pageRect = pageElement.getBoundingClientRect();
    const currentX = e.clientX - pageRect.left;
    const currentY = e.clientY - pageRect.top;
    
    // Calculate movement distance
    const deltaX = Math.abs(currentX - dragStart.x);
    const deltaY = Math.abs(currentY - dragStart.y);
    
    // Only start dragging if mouse has moved at least 5 pixels
    // This allows text selection for small movements
    if (!isDragging && (deltaX > 5 || deltaY > 5)) {
      setIsDragging(true);
      // Clear any text selection when starting to drag
      window.getSelection().removeAllRanges();
    }
    
    if (isDragging) {
      // Calculate the drag rectangle
      const left = Math.min(dragStart.x, currentX);
      const top = Math.min(dragStart.y, currentY);
      const width = Math.abs(currentX - dragStart.x);
      const height = Math.abs(currentY - dragStart.y);
      
      setDragRect({ top, left, width, height });
    }
  };
  
  const handleMouseUp = () => {
    // Handle case where dragRect may not be set yet
    if (isDragging) {
      if (dragRect && dragRect.width > 10 && dragRect.height > 10) {
        // Only set highlight if the drag area is significant (not just a click)
        setHighlight(dragRect);
        
        // Extract text from the highlighted area
        // Use cached reference to avoid repeated DOM queries
        const pageElement = pageElementRef.current;
        const textLayer = document.querySelector(PDF_TEXT_LAYER_SELECTOR);
        
        let extractedText = '';
        let isScannedPDF = false;
        
        if (pageElement && textLayer) {
          const textSpans = Array.from(textLayer.querySelectorAll('span'));
          
          // Check if this is a scanned PDF (no text spans found)
          if (textSpans.length === 0) {
            isScannedPDF = true;
          } else {
            const pageRect = pageElement.getBoundingClientRect();
            
            textSpans.forEach(span => {
              const spanRect = span.getBoundingClientRect();
              const spanRelativeTop = spanRect.top - pageRect.top;
              const spanRelativeLeft = spanRect.left - pageRect.left;
              const spanRelativeBottom = spanRect.bottom - pageRect.top;
              const spanRelativeRight = spanRect.right - pageRect.left;
              
              // Check if span intersects with the highlight rectangle
              const intersects = !(
                spanRelativeRight < dragRect.left ||
                spanRelativeLeft > dragRect.left + dragRect.width ||
                spanRelativeBottom < dragRect.top ||
                spanRelativeTop > dragRect.top + dragRect.height
              );
              
              if (intersects) {
                extractedText += (span.textContent || '') + ' ';
              }
            });
          }
        } else {
          // No text layer found - definitely a scanned PDF
          isScannedPDF = true;
        }
        
        setNewClauseText(extractedText.trim());
        setNewClausePage(String(pageNumber));
        
        // Handle scanned PDFs with automatic OCR
        if (isScannedPDF || extractedText.trim().length === 0) {
          // Clear any existing timer to prevent memory leaks
          if (scannedPdfNoticeTimerRef.current) {
            clearTimeout(scannedPdfNoticeTimerRef.current);
          }
          
          setShowScannedPdfNotice(true);
          
          // Automatically trigger OCR extraction
          // We need to do this after state is set, so use setTimeout
          setTimeout(async () => {
            setOcrLoading(true);
            try {
              console.log('Starting OCR extraction...', {
                pdfUrl: selectedDocUrl?.substring(0, 50),
                pageNumber,
                highlightRect: dragRect
              });
              
              const response = await fetch("/api/ocr-extract-text", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ 
                  pdfUrl: selectedDocUrl,
                  pageNumber: pageNumber,
                  highlightRect: dragRect
                }),
              });

              console.log('OCR API response status:', response.status);
              
              if (response.ok) {
                const data = await response.json();
                console.log('OCR API response data:', data);
                
                if (data.success && data.text) {
                  setNewClauseText(data.text);
                  setShowScannedPdfNotice(false);
                  console.log('OCR extraction successful, text length:', data.text.length);
                } else {
                  console.warn('OCR returned no text');
                  // Keep banner visible for manual retry
                }
              } else {
                // Try to get error details
                const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
                console.error('OCR API error:', response.status, errorData);
                alert(`OCR failed: ${errorData.error || 'Unknown error'}. You can retry or enter text manually.`);
              }
            } catch (error) {
              console.error("Auto-OCR Error:", error);
              alert(`OCR extraction failed: ${error.message}. You can retry or enter text manually.`);
              // Keep the banner visible if OCR fails
              // Auto-hide after delay to allow manual entry
              scannedPdfNoticeTimerRef.current = setTimeout(() => {
                setShowScannedPdfNotice(false);
                scannedPdfNoticeTimerRef.current = null;
              }, SCANNED_PDF_NOTICE_AUTO_HIDE_DELAY);
            } finally {
              setOcrLoading(false);
            }
          }, 100);
        } else {
          setShowScannedPdfNotice(false);
        }
      } else {
        // Small drag or click - clear highlight
        setHighlight(null);
        setNewClauseText("");
      }
    } else {
      // If not dragging, check for text selection (original behavior)
      const selection = window.getSelection();
      const text = selection.toString().trim();
      if (text) {
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        const pageElement = document.querySelector(PDF_PAGE_SELECTOR);
        const pageRect = pageElement ? pageElement.getBoundingClientRect() : null;

        if (pageRect) {
          // Calculate position relative to the page container
          const top = rect.top - pageRect.top; 
          const left = rect.left - pageRect.left;
          const width = rect.width;
          const height = rect.height;

          setHighlight({ top, left, width, height });
        } else {
          setHighlight(null);
        }
        setNewClauseText(text);
        setNewClausePage(String(pageNumber));
      }
    }
    
    // Reset drag state and clear cached reference
    setIsDragging(false);
    setDragStart(null);
    setDragRect(null);
    pageElementRef.current = null;
  };
  
  const handleMouseLeave = () => {
    // When mouse leaves, cancel the drag without creating a highlight
    if (isDragging) {
      setIsDragging(false);
      setDragStart(null);
      setDragRect(null);
      pageElementRef.current = null;
    }
  };

  const handlePageChange = (newPageNumber) => {
    setPageNumber(newPageNumber);
    setNewClausePage(String(newPageNumber));
    setHighlight(null);
    setNewClauseText("");
    setShowScannedPdfNotice(false);
    // Check if the new page is scanned (detection only, no auto-extraction)
    setTimeout(() => {
      checkAndDetectScannedPage().catch((error) => {
        console.error("Error in checkAndDetectScannedPage:", error);
      });
    }, PDF_RENDER_DELAY_MS);
  };
  
  // Handle document selection change
  const handleDocumentChange = (newDocUrl) => {
    setSelectedDocUrl(newDocUrl);
    setPageNumber(1);
    setNewClausePage("1");
    setHighlight(null);
    setNewClauseText("");
    setShowScannedPdfNotice(false);
    setFullPageText(""); // Clear previous extracted text
    setShowExtractedText(false);
    // The checkAndExtractScannedPage will be triggered by onDocumentLoadSuccess
  };

  // --- OCR Extraction for Scanned PDFs ---
  const handleOcrExtraction = async () => {
    if (!selectedDocUrl) {
      alert("Please select a document first.");
      return;
    }

    if (!highlight) {
      alert("Please drag to highlight an area first.");
      return;
    }

    setOcrLoading(true);
    setShowScannedPdfNotice(true);

    try {
      console.log('Manual OCR retry...', {
        pdfUrl: selectedDocUrl?.substring(0, 50),
        pageNumber,
        highlight
      });
      
      const response = await fetch("/api/ocr-extract-text", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          pdfUrl: selectedDocUrl,
          pageNumber: pageNumber,
          highlightRect: highlight
        }),
      });

      console.log('Manual OCR response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('OCR API error:', errorData);
        throw new Error(errorData.details || errorData.error || 'OCR extraction failed');
      }

      const data = await response.json();
      console.log('Manual OCR response data:', data);

      if (data.success && data.text) {
        setNewClauseText(data.text);
        setShowScannedPdfNotice(false);
        // Clear the timer when OCR succeeds
        if (scannedPdfNoticeTimerRef.current) {
          clearTimeout(scannedPdfNoticeTimerRef.current);
          scannedPdfNoticeTimerRef.current = null;
        }
        console.log('Manual OCR successful, text length:', data.text.length);
      } else {
        console.warn('OCR returned no text');
        alert("OCR extraction returned no text. Please try again or enter text manually.");
      }
    } catch (error) {
      console.error("Manual OCR Error:", error);
      alert(`OCR extraction failed: ${error.message}. Please enter text manually or check the console for details.`);
    } finally {
      setOcrLoading(false);
    }
  };

  // --- AI Extraction (current page) ---
  const handleAiExtraction = async () => {
    if (!selectedDocUrl) return alert("Please select a document first.");

    const textToAnalyze = await extractCurrentPageText(selectedDocUrl, pageNumber);

    if (!textToAnalyze || textToAnalyze.length < 50) {
      return alert("Page seems empty or text extraction failed. Try a different page.");
    }

    setAiLoading(true);
    let alertMessage = "";

    try {
      const response = await fetch("/api/extract-document", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          type: "clauses",
          documentText: textToAnalyze 
        }),
      });

      if (!response.ok) throw new Error("AI Request Failed");

      const data = await response.json();

      const validClauses = Array.isArray(data.clauses) ? data.clauses : [];

      if (validClauses.length > 0) {
        // Find highlight rectangles for each AI-extracted clause
        // Using the rendered text layer for pin-point accuracy
        const clausesWithHighlights = validClauses.map((c) => {
          // Use the page number from AI extraction or fall back to current page
          const clausePage = c.page ? parseInt(c.page, 10) : pageNumber;
          
          // Find the text position using the rendered text layer (only works for current page)
          let highlightRect = null;
          if (clausePage === pageNumber) {
            highlightRect = findTextPositionInRenderedPage(clausePage, c.text || "");
          }
          
          return {
            text: c.text || "Extracted Clause",
            page: String(clausePage),
            number: c.number || "N/A",
            status: "Open",
            comment: "",
            createdAt: new Date(),
            highlightRect: highlightRect,
            documentUrl: selectedDocUrl, // Store which document this clause belongs to
          };
        });

        // Save all clauses to Firestore
        const promises = clausesWithHighlights.map((clauseData) =>
          addDoc(collection(db, "tenders", id, "clauses"), clauseData)
        );
        await Promise.all(promises);
        fetchClauses();
        alertMessage = `Success! Extracted ${validClauses.length} clauses from Page ${pageNumber}.`;
        setNewClauseText("");
        setHighlight(null);
      } else {
        alertMessage = "AI analyzed the text but found no specific numbered clauses. Try selecting a different section.";
      }
    } catch (error) {
      console.error("AI Error:", error);
      alertMessage = "AI Extraction Failed (Network or API issue).";
    } finally {
      alert(alertMessage);
      setAiLoading(false);
    }
  };

  // --- HANDLERS ---
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditFormData(tender);
  };
  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const tenderRef = doc(db, "tenders", id);
      await updateDoc(tenderRef, editFormData);
      setTender(editFormData);
      setIsEditing(false);
    } catch (error) {
      console.error("handleSave error:", error);
      alert("Error saving tender.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleUploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploadLoading(true);
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const fileRef = ref(storage, `tenders/${id}/${file.name}`);
        await uploadBytes(fileRef, file);
        const url = await getDownloadURL(fileRef);
        return { name: file.name, url, size: file.size };
      });
      const newFileObjects = await Promise.all(uploadPromises);
      const tenderRef = doc(db, "tenders", id);
      await updateDoc(tenderRef, { files: arrayUnion(...newFileObjects) });
      setTender((prev) => {
        const updatedFiles = [...(prev.files || []), ...newFileObjects];
        if (!selectedDocUrl && updatedFiles.length > 0) setSelectedDocUrl(updatedFiles[0].url);
        return { ...prev, files: updatedFiles };
      });
      alert("Upload Successful!");
      setActiveTab("documents");
    } catch (err) {
      console.error("handleUploadFiles error:", err);
      alert("Error uploading files.");
    } finally {
      setUploadLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = null;
    }
  };

  const handleDeleteFile = async (fileToDelete) => {
    if (!window.confirm(`Remove ${fileToDelete.name}?`)) return;
    try {
      // remove from storage
      const urlPath = decodeURIComponent(fileToDelete.url.split("/o/")[1].split("?")[0]);
      const fileRef = ref(storage, urlPath);
      await deleteObject(fileRef).catch(() => {});
      // remove from tender document (uses arrayRemove)
      const tenderRef = doc(db, "tenders", id);
      await updateDoc(tenderRef, { files: arrayRemove(fileToDelete) });

      setTender((prev) => ({ ...prev, files: prev.files.filter((f) => f.url !== fileToDelete.url) }));
    } catch (error) {
      console.error("handleDeleteFile error:", error);
      alert("Error removing file.");
    }
  };

   const handleAddClause = async () => {
    if (!newClauseText) return;
    try {
      await addDoc(collection(db, "tenders", id, "clauses"), {
        text: newClauseText,
        page: newClausePage,
        number: newClauseNumber || "",
        status: "Open",
        comment: "",
        createdAt: new Date(),
        highlightRect: highlight || null,
        documentUrl: selectedDocUrl, // Store which document this clause belongs to
      });
      setNewClauseText("");
      setNewClauseNumber("");
      setHighlight(null);
      setShowScannedPdfNotice(false);
      fetchClauses();
    } catch (error) {
      console.error("handleAddClause error:", error);
    }
  };

  const updateClauseStatus = async (clauseId, newStatus) => {
    try {
      const clauseRef = doc(db, "tenders", id, "clauses", clauseId);
      await updateDoc(clauseRef, { status: newStatus });
      setClauses((prev) => prev.map((c) => (c.id === clauseId ? { ...c, status: newStatus } : c)));
    } catch (error) {
      console.error("updateClauseStatus error:", error);
    }
  };

  // --- COMMENT LOGIC ---
  const handleCommentChange = (clauseId, newComment) => {
    setClauses((prev) => prev.map((c) => (c.id === clauseId ? { ...c, comment: newComment } : c)));
  };

  const handleSaveComment = async (clause) => {
    const { id: clauseId, comment, text, customerResponses } = clause;
    setIsCommentSaving((prev) => ({ ...prev, [clauseId]: true }));
    try {
      const clauseRef = doc(db, "tenders", id, "clauses", clauseId);
      await updateDoc(clauseRef, { comment: comment });
      
      // Save to knowledge base for future suggestions
      const latestResponse = customerResponses ? Object.values(customerResponses).filter(r => r?.trim()).pop() : "";
      await saveToKnowledgeBase(text, comment, latestResponse, "Rev 0");
    } catch (error) {
      console.error("handleSaveComment error:", error);
      alert("Failed to save comment.");
    } finally {
      setIsCommentSaving((prev) => {
        const newState = { ...prev };
        delete newState[clauseId];
        return newState;
      });
    }
  };

  const handleAIGenerateComment = async (clause) => {
    const { id: clauseId, text, status, comment } = clause;
    const textToRefine = comment && comment.trim().length > 10 ? comment : text;
    if (status === "Pending" && (!comment || comment.trim().length < 10)) {
      return alert("Please set a compliance status OR type your idea into the comment box first.");
    }

    setIsCommentSaving((prev) => ({ ...prev, [clauseId]: true }));
    try {
      const response = await fetch("/api/generate-comments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "simple",
          clauseText: textToRefine,
          status: status,
          tenderTitle: tender?.title,
          tenderDescription: tender?.description,
          userComment: comment,
        }),
      });

      if (!response.ok) throw new Error("AI Comment Generation Failed.");

      const data = await response.json();
      const aiComment = data.comment || "AI failed to generate a useful comment.";

      const clauseRef = doc(db, "tenders", id, "clauses", clauseId);
      await updateDoc(clauseRef, { comment: aiComment });

      setClauses((prev) => prev.map((c) => (c.id === clauseId ? { ...c, comment: aiComment } : c)));
    } catch (error) {
      console.error("handleAIGenerateComment error:", error);
      alert("Failed to generate AI comment.");
    } finally {
      setIsCommentSaving((prev) => {
        const newState = { ...prev };
        delete newState[clauseId];
        return newState;
      });
    }
  };

  const deleteClause = async (clauseId) => {
    if (!window.confirm("Delete this clause?")) return;
    try {
      await deleteDoc(doc(db, "tenders", id, "clauses", clauseId));
      setClauses((prev) => prev.filter((c) => c.id !== clauseId));
    } catch (error) {
      console.error("deleteClause error:", error);
    }
  };

  // --- EXPORT CLAUSES TO CSV (local) ---
  const handleExportClauses = () => {
    if (clauses.length === 0) {
      return alert("No clauses to export.");
    }

    const headers = ["Tender ID", "Clause Number", "Page", "Status", "Clause Text", "Comment", "Date Created"];

    const rows = clauses.map((c) => [
      tender.id,
      `"${c.number || ""}"`,
      c.page,
      c.status,
      `"${(c.text || "").replace(/"/g, '""')}"`,
      `"${(c.comment || "").replace(/"/g, '""')}"`,
      c.createdAt && c.createdAt.seconds ? new Date(c.createdAt.seconds * 1000).toLocaleDateString() : (c.createdAt ? new Date(c.createdAt).toLocaleDateString() : ""),
    ]);

    const csvContent = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `${(tender.clientName || "tender").replace(/\s/g, "_")}_${tender.id}_Compliance.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      alert("Browser does not support automatic CSV download.");
    }
  };

  // --- Persistence hooks passed to Review component ---

  // Save a customer response for a clause and revision (e.g., "Rev 0")
  const onSaveCustomerResponse = async (clauseId, revisionName, text) => {
    if (!clauseId || !revisionName) throw new Error("Missing clauseId or revisionName");
    try {
      const clauseRef = doc(db, "tenders", id, "clauses", clauseId);
      const fieldKey = `customerResponses.${revisionName}`;
      await updateDoc(clauseRef, { [fieldKey]: text });
      // update local state
      const updatedClauses = clauses.map((c) => (c.id === clauseId ? { ...c, customerResponses: { ...(c.customerResponses || {}), [revisionName]: text } } : c));
      setClauses(updatedClauses);
      
      // Save to knowledge base for future suggestions
      const clause = updatedClauses.find(c => c.id === clauseId);
      if (clause) {
        const comment = clause.comment || (clause.comments ? Object.values(clause.comments).filter(c => c?.trim()).pop() : "");
        await saveToKnowledgeBase(clause.text, comment, text, revisionName);
      }
    } catch (err) {
      console.error("onSaveCustomerResponse error:", err);
      throw err;
    }
  };

  // Bulk upload handler for responses/comments
  const onBulkUploadResponse = async (updates) => {
    if (!updates || updates.length === 0) return;
    
    try {
      const promises = updates.map(async (update) => {
        const clauseRef = doc(db, "tenders", id, "clauses", update.id);
        if (update.type === 'response') {
          const fieldKey = `customerResponses.${update.revName}`;
          await updateDoc(clauseRef, { [fieldKey]: update.value });
        } else if (update.type === 'comment') {
          const fieldKey = `comments.${update.revName}`;
          await updateDoc(clauseRef, { [fieldKey]: update.value });
        }
      });
      
      await Promise.all(promises);
      
      // Update local state
      setClauses((prev) => {
        return prev.map((c) => {
          const update = updates.find(u => u.id === c.id);
          if (!update) return c;
          
          if (update.type === 'response') {
            return {
              ...c,
              customerResponses: { ...(c.customerResponses || {}), [update.revName]: update.value }
            };
          } else if (update.type === 'comment') {
            return {
              ...c,
              comments: { ...(c.comments || {}), [update.revName]: update.value }
            };
          }
          return c;
        });
      });
      
      // Save all updates to knowledge base concurrently
      const kbPromises = updates.map((update) => {
        const clause = clauses.find(c => c.id === update.id);
        if (clause) {
          const comment = update.type === 'comment' ? update.value : (clause.comment || "");
          const response = update.type === 'response' ? update.value : "";
          return saveToKnowledgeBase(clause.text, comment, response, update.revName);
        }
        return Promise.resolve();
      });
      await Promise.all(kbPromises);
    } catch (error) {
      console.error("onBulkUploadResponse error:", error);
      throw error;
    }
  };

  // Add a revision name to tender.revisions (persisted)
  const onAddRevision = async (revisionName) => {
    if (!revisionName) return;
    try {
      const tenderRef = doc(db, "tenders", id);
      await updateDoc(tenderRef, {
        revisions: arrayUnion({ name: revisionName, timestamp: new Date().toISOString() }),
      });
      // refresh tender and local columns/clauses in caller (Review will fetch tender.revisions via tender prop)
      await fetchTender();
    } catch (err) {
      console.error("onAddRevision error:", err);
      throw err;
    }
  };

  // Export handler passed into Review (calls CSV or PDF as requested)
  const onExportClauses = async (type) => {
    if (!type || type === "csv") {
      handleExportClauses();
    } else if (type === "pdf") {
      // Build a printable window and trigger print (user can Save as PDF)
      const visibleClauses = clauses;
      const head = ["Sr No", "Clause Text", "Comment(s)", "Status"];
      const rowsHtml = visibleClauses
        .map((c, idx) => {
          const comments = c.comments ? Object.entries(c.comments).map(([k, v]) => `<strong>${k}:</strong> ${v}`).join("<br/>") : (c.comment || "");
          return `<tr>
            <td style="padding:8px;border:1px solid #ddd;">${idx + 1}</td>
            <td style="padding:8px;border:1px solid #ddd;">${escapeHtml(c.text || "")}</td>
            <td style="padding:8px;border:1px solid #ddd;">${escapeHtml(comments || "—")}</td>
            <td style="padding:8px;border:1px solid #ddd;">${escapeHtml(c.status || "Pending")}</td>
          </tr>`;
        })
        .join("");

      const html = `
        <html>
          <head><title>Export</title></head>
          <body>
            <h2>${escapeHtml(tender?.title || tender?.clientName || "Tender")}</h2>
            <table style="border-collapse:collapse;width:100%;font-family:Arial,Helvetica,sans-serif">
              <thead><tr>${head.map((h) => `<th style="padding:8px;border:1px solid #ddd;background:#f1f5f9">${escapeHtml(h)}</th>`).join("")}</tr></thead>
              <tbody>${rowsHtml}</tbody>
            </table>
          </body>
        </html>
      `;
      const w = window.open("", "_blank");
      if (!w) {
        alert("Popup blocked. Allow popups to export PDF.");
        return;
      }
      w.document.open();
      w.document.write(html);
      w.document.close();
      setTimeout(() => w.print(), 400);
    } else {
      alert("Unsupported export type.");
    }
  };

  function escapeHtml(unsafe) {
    if (!unsafe && unsafe !== 0) return "";
    return String(unsafe).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }

  // --- Rendering / UI ---
  // The filteredClauses definition was moved up before the useEffect hook.

  if (loading) return <div className="dashboard-container"><h2>Loading...</h2></div>;
  if (!tender) return <div className="dashboard-container"><h2>Not found</h2></div>;

  const docCount = (tender.files ? tender.files.length : 0) + (tender.attachmentUrl ? 1 : 0);
  const complianceRate = clauses.length > 0 ? Math.round((clauses.filter((c) => c.status === "Complied").length / clauses.length) * 100) : 0;
  
  // Dynamic placeholder text for clause textarea
  const clauseTextPlaceholder = showScannedPdfNotice 
    ? "Type clause text here (scanned PDF)..." 
    : "Select text in PDF or type here...";

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "documents", label: `Documents (${docCount})` },
    { id: "compliance", label: `Compliance (${complianceRate}%)` },
    { id: "compliance-review", label: "Compliance Review" },    
    { id: "costing-sheet", label: "Costing Sheet" },
    { id: "vendor-automation", label: "🏭 Vendor Management" },
    { id: "sld", label: "📐 System Architecture" },
    { id: "simple-proposal", label: "📄 Proposal" },
  ];

  return (
    <div className="dashboard-container">
      <button onClick={() => navigate("/dashboard")} style={{ background: "none", border: "none", cursor: "pointer", marginBottom: "10px", fontSize: "16px", color: "#555", fontWeight: "500" }}>← Back to Dashboard</button>

      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "5px" }}>
        <h1 style={{ margin: 0 }}>{tender.clientName}</h1>
        <span style={{ background: "#e0f2fe", color: "#0284c7", padding: "4px 12px", borderRadius: "15px", fontSize: "12px", fontWeight: "600" }}>{tender.status}</span>
      </div>

      <div style={{ color: "#666", marginBottom: "20px", fontSize: "14px" }}>{tender.title} • {tender.currency} {Number(tender.value || 0).toLocaleString()}</div>

      <div className="tabs-container">
        {tabs.map((tab) => (
          <button key={tab.id} className={`tab-btn ${activeTab === tab.id ? "active" : ""}`} onClick={() => setActiveTab(tab.id)}>{tab.label}</button>
        ))}
      </div>

      {/* Overview */}
      {activeTab === "overview" && (
        <div className="detail-card">
          <div className="detail-card-header">
            <h3 style={{ marginTop: 0 }}>Tender Details</h3>
            {!isEditing && <button className="new-btn" onClick={() => setIsEditing(true)}>Edit</button>}
          </div>
          {!isEditing ? (
            <div className="details-grid">
              <div className="detail-item"><label>RFP Number</label><div>{tender.rfpNumber || "N/A"}</div></div>
              <div className="detail-item"><label>Stage</label><div>{tender.status}</div></div>
              <div className="detail-item"><label>Status</label><div>Active</div></div>
              <div className="detail-item"><label>Deadline</label><div>{tender.deadline}</div></div>
              <div className="detail-item"><label>Issuing Organization</label><div>{tender.issuingOrg || "N/A"}</div></div>
              <div className="detail-item"><label>Contact Person</label><div>{tender.contactPerson || "N/A"}</div></div>
              <div className="detail-item"><label>Project Country</label><div>{tender.projectCountry || "N/A"}</div></div>
            </div>
          ) : (
            <form onSubmit={handleSave}>
              <div className="form-group" style={{ marginBottom: '15px' }}><label>Tender Title</label><input name="title" value={editFormData.title || ''} onChange={handleEditChange} required /></div>
              <div className="form-grid">
                <div className="form-group"><label>Client Name</label><input name="clientName" value={editFormData.clientName || ''} onChange={handleEditChange} required /></div>
                <div className="form-group"><label>RFP Number</label><input name="rfpNumber" value={editFormData.rfpNumber || ''} onChange={handleEditChange} /></div>
              </div>
              <div className="form-grid">
                <div className="form-group"><label>Estimated Value</label><input type="number" name="value" value={editFormData.value || ''} onChange={handleEditChange} /></div>
                <div className="form-group"><label>Currency</label>
                  <select name="currency" value={editFormData.currency || 'USD'} onChange={handleEditChange}>
                    <option value="USD">USD</option><option value="EUR">EUR</option><option value="GBP">GBP</option><option value="SAR">SAR</option><option value="AED">AED</option>
                  </select>
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: '15px' }}><label>Deadline</label><input type="datetime-local" name="deadline" value={editFormData.deadline || ''} onChange={handleEditChange} /></div>
              <div className="form-grid">
                <div className="form-group"><label>Issuing Organization</label><input name="issuingOrg" value={editFormData.issuingOrg || ''} onChange={handleEditChange} /></div>
                <div className="form-group"><label>Contact Person</label><input name="contactPerson" value={editFormData.contactPerson || ''} onChange={handleEditChange} /></div>
              </div>
              <div className="form-group" style={{ marginBottom: '15px' }}><label>Project Country</label><input name="projectCountry" value={editFormData.projectCountry || ''} onChange={handleEditChange} placeholder="Enter project country for duty/shipping calculations" /></div>
              <div className="form-group" style={{ marginBottom: '15px' }}><label>Description</label><textarea name="description" value={editFormData.description || ''} onChange={handleEditChange} rows="3" /></div>

              <div className="edit-form-actions">
                <button type="button" className="cancel-btn" onClick={handleCancelEdit} disabled={isSaving}>Cancel</button>
                <button type="submit" className="new-btn" disabled={isSaving}>{isSaving ? "Saving..." : "Save Changes"}</button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Documents, Compliance and Compliance Review tabs below (unchanged) */}
      {activeTab === "documents" && (
        <DocumentManager
          tenderId={id}
          tender={tender}
          onTenderUpdate={fetchTender}
        />
      )}

      {activeTab === "compliance" && (
        <div className="compliance-container">
          <div 
            className="pdf-viewer-pane" 
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            style={{ userSelect: isDragging ? 'none' : 'text', cursor: isDragging ? 'crosshair' : 'default' }}
          >
            <div className="pdf-header">
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <select value={selectedDocUrl} onChange={(e) => handleDocumentChange(e.target.value)} className="doc-selector">
                  <option value="">Select Document...</option>
                  {tender.files && tender.files.map((f, i) => (<option key={i} value={f.url}>{f.name}</option>))}
                </select>
                {console.log('Button render check - isPageScanned:', isPageScanned, 'selectedDocUrl:', selectedDocUrl) || null}
                {isPageScanned && (
                  <button 
                    className="pdf-btn"
                    onClick={handleManualFullPageExtraction}
                    disabled={isExtractingFullPage}
                    style={{
                      background: isExtractingFullPage ? '#cbd5e1' : '#3b82f6',
                      color: 'white',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: isExtractingFullPage ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                    title="Extract all text from this scanned page using AI OCR"
                  >
                    {isExtractingFullPage ? (
                      <>🔄 Extracting...</>
                    ) : (
                      <>📄 Extract Text</>
                    )}
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: '5px' }}>
                <button className="pdf-btn" disabled={pageNumber <= 1} onClick={() => handlePageChange(pageNumber - 1)}>Prev</button>
                <span style={{ fontSize: '12px', alignSelf: 'center' }}>Page {pageNumber} of {numPages}</span>
                <button className="pdf-btn" disabled={pageNumber >= numPages} onClick={() => handlePageChange(pageNumber + 1)}>Next</button>
              </div>
            </div>

            <div className="pdf-scroll-area">
              {selectedDocUrl ? (
                // IMPORTANT: The custom highlight rendering has been removed from JSX
                // and implemented in the useEffect hook above for DOM injection.
                // This div no longer needs position: 'relative'.
                <div>
                  <Document file={selectedDocUrl} onLoadSuccess={onDocumentLoadSuccess} onLoadError={onDocumentLoadError} loading={<div style={{ color: 'white' }}>Loading PDF...</div>}>
                    <Page pageNumber={pageNumber} width={PDF_RENDER_WIDTH} />
                  </Document>
                </div>
              ) : <div className="pdf-placeholder">Select a document</div>}
            </div>
          </div>

          <div className="clauses-pane">
            <div className="clauses-header">
              <h3>Compliance Checklist</h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="new-btn ai-btn" onClick={handleAiExtraction} disabled={aiLoading}>{aiLoading ? "🤖 Analyzing..." : "✨ AI Extract"}</button>
                <button className="cancel-btn" onClick={handleExportClauses} style={{ background: 'white', border: '1px solid #cbd5e1', padding: '6px 12px', fontWeight: 600 }}>⬇ Download All</button>
              </div>
            </div>

            {showScannedPdfNotice && highlight && (
              <div style={{ 
                margin: '15px', 
                padding: '12px', 
                background: ocrLoading ? '#d1ecf1' : '#fff3cd', 
                border: ocrLoading ? '1px solid #17a2b8' : '1px solid #ffc107', 
                borderRadius: '6px',
                fontSize: '13px',
                color: ocrLoading ? '#0c5460' : '#856404',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px'
              }}>
                <span style={{ fontSize: '20px' }} aria-label="Document icon" role="img" title="Document icon">
                  {ocrLoading ? '🔄' : '📝'}
                </span>
                <div style={{ flex: 1 }}>
                  <strong>{ocrLoading ? 'Extracting Text with OCR...' : 'Scanned PDF Detected'}</strong>
                  <p style={{ margin: '5px 0 0 0' }}>
                    {ocrLoading 
                      ? 'Using AI to extract text from the highlighted area. This may take a few seconds...'
                      : 'OCR extraction is in progress. If it fails, you can manually type the text below.'
                    }
                  </p>
                  {!ocrLoading && (
                    <button 
                      onClick={handleOcrExtraction}
                      disabled={ocrLoading}
                      style={{ 
                        marginTop: '8px',
                        padding: '4px 10px',
                        background: '#ffc107',
                        border: '1px solid #ff9800',
                        borderRadius: '4px',
                        color: '#000',
                        fontSize: '12px',
                        cursor: 'pointer',
                        fontWeight: 600
                      }}
                    >
                      🔄 Retry OCR
                    </button>
                  )}
                </div>
                <button 
                  onClick={() => setShowScannedPdfNotice(false)}
                  aria-label="Close notice"
                  title="Close notice"
                  disabled={ocrLoading}
                  style={{ 
                    background: 'transparent', 
                    border: 'none', 
                    fontSize: '18px', 
                    cursor: ocrLoading ? 'not-allowed' : 'pointer',
                    color: ocrLoading ? '#999' : (ocrLoading ? '#0c5460' : '#856404'),
                    padding: '0 5px',
                    opacity: ocrLoading ? 0.5 : 1
                  }}
                >×</button>
              </div>
            )}

            {/* Full-page extracted text display for scanned PDFs */}
            {(isExtractingFullPage || fullPageText) && (
              <div 
                role="region" 
                aria-label="Extracted text from scanned PDF"
                style={{ 
                  margin: '15px', 
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  background: '#ffffff'
                }}
              >
                <button
                  onClick={() => setShowExtractedText(!showExtractedText)}
                  aria-expanded={showExtractedText}
                  aria-controls="extracted-text-content"
                  style={{ 
                    width: '100%',
                    padding: '12px 15px', 
                    background: isExtractingFullPage ? '#dbeafe' : '#f0fdf4',
                    borderBottom: showExtractedText ? '1px solid #d1d5db' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    userSelect: 'none',
                    border: 'none',
                    textAlign: 'left'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '20px' }} aria-hidden="true">
                      {isExtractingFullPage ? '🔄' : '📄'}
                    </span>
                    <div>
                      <strong style={{ color: isExtractingFullPage ? '#1e40af' : '#166534', fontSize: '14px' }}>
                        {isExtractingFullPage ? 'Extracting Text from Scanned PDF...' : 'Extracted Text from Page'}
                      </strong>
                      {!isExtractingFullPage && fullPageText && (
                        <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
                          {fullPageText.length} characters extracted • Click to {showExtractedText ? 'hide' : 'view'}
                        </p>
                      )}
                    </div>
                  </div>
                  {!isExtractingFullPage && fullPageText && (
                    <span style={{ fontSize: '14px', color: '#6b7280' }} aria-hidden="true">
                      {showExtractedText ? '▼' : '▶'}
                    </span>
                  )}
                </button>
                
                {showExtractedText && fullPageText && !isExtractingFullPage && (
                  <div 
                    id="extracted-text-content"
                    role="article"
                    aria-label="Extracted text content"
                    style={{ 
                      padding: '15px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      background: '#f9fafb',
                      fontSize: '13px',
                      lineHeight: '1.6',
                      color: '#374151',
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'monospace'
                    }}
                  >
                    {fullPageText}
                  </div>
                )}
                
                {isExtractingFullPage && (
                  <div 
                    role="status"
                    aria-live="polite"
                    aria-busy="true"
                    aria-label={`Extracting text from page ${pageNumber}. This may take 5 to 10 seconds.`}
                    style={{ 
                      padding: '20px',
                      textAlign: 'center',
                      color: '#6b7280',
                      fontSize: '13px'
                    }}
                  >
                    <div style={{ marginBottom: '10px' }}>
                      <span aria-hidden="true">⏳</span> Please wait...
                    </div>
                    <div>Analyzing page {pageNumber} with AI OCR (typically 5-10 seconds)</div>
                  </div>
                )}
              </div>
            )}

            <div className="add-clause-box">
              <textarea 
                placeholder={clauseTextPlaceholder} 
                value={newClauseText} 
                onChange={(e) => setNewClauseText(e.target.value)} 
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px', gap: '10px' }}>
                <input type="text" placeholder="Clause #" value={newClauseNumber} onChange={(e) => setNewClauseNumber(e.target.value)} style={{ width: '90px' }} />
                <input type="number" placeholder="Pg #" value={newClausePage} onChange={(e) => setNewClausePage(e.target.value)} style={{ width: '60px' }} />
                <button className="new-btn" style={{ padding: '5px 10px', flexGrow: 1 }} onClick={handleAddClause}>+ Add Manual</button>
              </div>
            </div>

            <div style={{ padding: '0 15px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '13px', color: '#475569' }}>
              <span>
                {showAllClauses 
                  ? `Showing All Pages for Current Document (${filteredClauses.length})` 
                  : `Showing Clauses for Page ${pageNumber} (${filteredClauses.length})`
                }
              </span>
              <label>
                <input type="checkbox" checked={showAllClauses} onChange={(e) => setShowAllClauses(e.target.checked)} style={{ marginRight: '5px' }} />
                Show All
              </label>
            </div>

            <div className="clauses-list">
              {filteredClauses.map((clause) => (
                <div key={clause.id} className={`clause-card ${clause.status}`}>
                  <div className="clause-meta">
                    <span className="page-tag">ID: {clause.number}</span>
                    <span className="page-tag">Pg {clause.page}</span>
                    <button className="delete-x" onClick={() => deleteClause(clause.id)}>×</button>
                  </div>
                  <p>{clause.text}</p>

                  <div style={{ marginBottom: '8px' }}>
                    <select className="status-selector" value={clause.status} onChange={(e) => updateClauseStatus(clause.id, e.target.value)}>
                      <option value="Pending">⏳ Pending</option>
                      <option value="Complied">✅ Complied</option>
                      <option value="Non-Complied">❌ Non-Complied</option>
                      <option value="Partially Complied">🟡 Partially Complied</option>
                      <option value="Not Applicable">⚪ Not Applicable</option>
                    </select>
                  </div>

                  <div className="comment-area">
                    <textarea placeholder="Enter compliance comment here..." value={clause.comment || ''} onChange={(e) => handleCommentChange(clause.id, e.target.value)} />
                    <div className="comment-actions">
                      <button className="ai-comment-btn" onClick={() => handleAIGenerateComment(clause)} disabled={isCommentSaving[clause.id] || clause.status === 'Pending'} style={{ marginRight: '8px', opacity: (isCommentSaving[clause.id] || clause.status === 'Pending') ? 0.6 : 1 }}>
                        {isCommentSaving[clause.id] ? "Saving..." : "✨ AI Justify"}
                      </button>
                      <button className="ai-comment-btn" onClick={() => handleSaveComment(clause)} disabled={isCommentSaving[clause.id]}>
                        Save Note
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* --- COMPLIANCE REVIEW TAB (Review component with persistence hooks) --- */}
      {activeTab === "compliance-review" && (
        <Review
          tender={tender}
          clauses={clauses}
          onUpdateStatus={updateClauseStatus}
          onCommentChange={handleCommentChange}
          onSaveComment={handleSaveComment}
          onAIGenerateComment={handleAIGenerateComment}
          onDeleteClause={deleteClause}
          onExportClauses={onExportClauses}
          isCommentSaving={isCommentSaving}
          onSaveCustomerResponse={onSaveCustomerResponse}
          onAddRevision={onAddRevision}
          onBulkUploadResponse={onBulkUploadResponse}
          findSimilarClauses={findSimilarClauses}
        />
      )}

      {/* --- COSTING SHEET TAB --- */}
      {activeTab === "costing-sheet" && (
        <CostingSheet tenderId={id} tender={tender} />
      )}

      {/* --- VENDOR AUTOMATION TAB --- */}
      {activeTab === "vendor-automation" && (
        <VendorAutomation tenderId={id} tender={tender} />
      )}

      {/* --- SINGLE LINE DIAGRAM TAB --- */}
      {activeTab === "sld" && (
        <DiagramsNetEditor tenderId={id} tender={tender} costingItems={costingItems} />
      )}

      {/* --- SIMPLE PROPOSAL GENERATOR TAB --- */}
      {activeTab === "simple-proposal" && (
        <SimpleProposalGenerator tenderId={id} />
      )}

      {/* Fallback content for other tabs */}
      {activeTab !== "overview" && activeTab !== "documents" && activeTab !== "compliance" && activeTab !== "compliance-review" && activeTab !== "costing-sheet" && activeTab !== "vendor-automation" && activeTab !== "sld" && activeTab !== "simple-proposal" && (
        <div className="detail-card"><h3>{activeTab}</h3><p>Coming soon!</p></div>
      )}
    </div>
  );
}
